/**
 * YC demo flow, delete after the demo (Leo 10-30 mockup): recommend, accept, authorize, case. Nothing is posted:
 * the steps live in this tab's sessionStorage, and the case is drawn into the building's docket from them.
 */
import * as React from "react";

import { usePortalFeaturesQuery } from "@/components/portal/usePortalFeatures";
import type {
  ViolationCase,
  ViolationCasesResponse,
} from "@/queries/violationCasesQueries";
import { CLOCK_KEYS, JUDGMENT_OWED } from "@/components/portal/casesDocket";
import { FINE, OVERDUE_DAYS, YC_TICKET } from "./ycDemo";

export const RESPONDENT = "20-05 SHORE BLVD OWNER LLC";
export const CASE_REF = "CC-160";
export const CASE_ID = "yc-demo-037030627L";
/** The ticket's charge code (ViolationMatter.charge_code). */
const CHARGE = "B179";

export type YcStage = "rec" | "auth" | "sign" | "on";
export type YcRole = "officer" | "managing_agent";
export interface YcState {
  stage: YcStage;
  declined: boolean;
  name: string;
  role: YcRole;
  /** ISO times of the clicks; the case and its activity read them. */
  accepted: string | null;
  authorized: string | null;
  prefilled: string | null;
  /** The building the ticket sits on, from the feed item at Accept. */
  buildingId: string | null;
  /** Its street name, for the sidebar rail's group line. */
  buildingLabel?: string | null;
}

const KEY = "yc-flow-1030";
const START: YcState = {
  stage: "rec",
  declined: false,
  name: "",
  role: "officer",
  accepted: null,
  authorized: null,
  prefilled: null,
  buildingId: null,
};
/** The agent's prefill lands this long after Authorize. */
const PREFILL_MS = 2500;

const load = (): YcState => {
  try {
    const raw = sessionStorage.getItem(KEY);
    return raw ? { ...START, ...JSON.parse(raw) } : START;
  } catch {
    return START;
  }
};
let state: YcState = START;
let loaded = false;
const subs = new Set<() => void>();
const read = (): YcState => {
  if (!loaded) {
    loaded = true;
    state = load();
  }
  return state;
};
export const setYc = (patch: Partial<YcState>): void => {
  state = { ...read(), ...patch };
  try {
    sessionStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    // Private mode: the flow still runs for this page view.
  }
  subs.forEach((f) => f());
};
let prefill: ReturnType<typeof setTimeout> | undefined;
/** Tests only; a fresh tab is a fresh demo (sessionStorage). */
export const resetYc = (): void => {
  clearTimeout(prefill);
  loaded = true;
  state = START;
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // Nothing stored.
  }
  subs.forEach((f) => f());
};
const subscribe = (f: () => void) => {
  subs.add(f);
  return () => {
    subs.delete(f);
  };
};
export const useYc = (): YcState =>
  React.useSyncExternalStore(subscribe, read, read);

const now = () => new Date().toISOString();
export const accept = (buildingId: string, buildingLabel?: string): void =>
  setYc({
    stage: "auth",
    declined: false,
    accepted: now(),
    buildingId,
    buildingLabel,
  });
export const authorize = (name: string, role: YcRole): void => {
  setYc({ stage: "on", name: name.trim(), role, authorized: now() });
  prefill = setTimeout(() => setYc({ prefilled: now() }), PREFILL_MS);
};

export const REC_TITLE = `Reschedule OATH hearing date to avoid ${FINE} fine.`;

export interface WhyRow {
  k: string;
  t: string;
  favors: boolean;
}
export const WHY: readonly WhyRow[] = [
  {
    k: "Money",
    t: `A new date gives time to cure and certify, which can avoid the ${FINE} fine.`,
    favors: true,
  },
  {
    k: "Deadline",
    t: `${OVERDUE_DAYS} days overdue. The request has to be filed soon to stay eligible.`,
    favors: true,
  },
  { k: "Effort", t: "Paperwork only. We file it; you approve.", favors: true },
  {
    k: "Blocks",
    t: "An unpaid judgment can become a lien and show up in a sale or refinance.",
    favors: false,
  },
];
export const CHANGE =
  "if the conditions can't be fixed before a new hearing, paying is simpler.";
export const BASED_ON = `Based on: OATH record ${YC_TICKET} · Strategy: balanced`;

export interface YcStep {
  key: string;
  label: string;
  /** The "If you accept" line; Authorize has none there. */
  offer: string | null;
  /** The line once accepted (Step N of 5). */
  t: string;
  who: string;
}
/** The one step list: the If-you-accept list, the progress bar and the running steps all read it. */
export const STEPS: readonly YcStep[] = [
  {
    key: "authorize",
    label: "Authorize",
    offer: null,
    t: "Authorize CodeClear to act for the owner",
    who: "You",
  },
  {
    key: "request",
    label: "Request",
    offer: "Request a new OATH hearing date and file it today",
    t: "Filing the new hearing request with OATH",
    who: "Correspondence",
  },
  {
    key: "confirm",
    label: "Confirm",
    offer: "Confirm the new date and put it on your calendar",
    t: "Confirm the new date and add it to your calendar",
    who: "Tracking",
  },
  {
    key: "fix",
    label: "Fix",
    offer: "Fix and certify the conditions before the new date",
    t: "Fix and certify before the new date",
    who: "Filing",
  },
  {
    key: "hearing",
    label: "Hearing",
    offer: "Attend the hearing for you with the certificate",
    t: "Attend the hearing with the certificate",
    who: "CodeClear",
  },
];
/** Index of the step in progress, and its "Step N of 5" title. */
export const stepAt = (s: YcState): number => (s.authorized ? 1 : 0);
export const STEP_TITLE = ["Authorize CodeClear", "Requesting a new hearing"];

export const AUTH_LEAD =
  "To request a new hearing date and appear at OATH for you, we need your authorization to act for the owner on this violation.";
export const AUTH_NOTE = `For ${RESPONDENT}. Once per respondent; it also covers this owner's future OATH items. Then we take it from here.`;
/** constants/ll33_rep.py ESRA_LINE, the line every authorization signs. */
export const ESRA =
  "By clicking Authorize, I sign this authorization electronically.";
/** `short`: the signed-by line, the activity and the PDF. */
export const CAPACITIES: { value: YcRole; label: string; short: string }[] = [
  {
    value: "officer",
    label: "Officer, member or manager of the Owner or of its general partner",
    short: "Officer",
  },
  {
    value: "managing_agent",
    label: "Managing agent in control of the premises (Admin Code 28-101.5)",
    short: "Managing agent",
  },
];
export const roleShort = (r: YcRole): string =>
  CAPACITIES.find((c) => c.value === r)?.short ?? "";
/** The statement, bold where the mockup is; the PDF prints the same words. */
export const authParts = (name: string): [string, boolean][] => [
  ["I, ", false],
  [name.trim() || "[your full legal name]", true],
  [", authorize CodeClear to act for ", false],
  [RESPONDENT, true],
  [" on OATH violation ", false],
  [YC_TICKET, true],
  [
    ": to request a new hearing date, file and receive papers, and appear at the hearing. I can revoke this at any time.",
    false,
  ],
];
export const authText = (name: string): string =>
  authParts(name)
    .map(([t]) => t)
    .join("");

/** Happening now, on the case. */
export const happening = (s: YcState): string => {
  if (!s.authorized) return "Waiting on your authorization.";
  if (!s.prefilled) return "Prefilling the OATH reschedule request.";
  return "OATH reschedule request prefilled. Ready for your signature and submission.";
};

/** The reopen variant's own keys (caseStageLabels.ts); labels null, so the working-title note shows. */
const CASE_STAGES = [
  "defaulted",
  "reopen_filing",
  "reopen_decision",
  "resolved",
];
const CASE_AT = 1;
const STATUS = ["complete", "current", "pending"] as const;

/** The demo case, drawn through the docket's own components. */
export const ycCase = (s: YcState): ViolationCase => ({
  caseId: CASE_ID,
  caseReference: CASE_REF,
  caseDisplaySubject: "Reschedule OATH hearing",
  buildingId: s.buildingId ?? "",
  patternKey: CHARGE,
  status: "OPEN",
  title: "Reschedule OATH hearing",
  createdAt: s.accepted,
  spine: {
    caseId: CASE_ID,
    variant: "reopen",
    branches: { reopen_decision: ["granted", "denied_settle_or_pay"] },
    stages: CASE_STAGES.map((key, i) => ({
      key,
      label: null,
      status: STATUS[Math.sign(i - CASE_AT) + 1],
      countDone: i < CASE_AT ? 1 : 0,
      countTotal: 1,
    })),
    tickets: [
      {
        ticketNumber: YC_TICKET,
        matterId: CASE_ID,
        stageKey: CASE_STAGES[CASE_AT],
        stageIndex: CASE_AT,
        filed: false,
        decided: false,
        outcome: "Defaulted",
        certificationStatus: "Due",
        nextDeadlineAt: null,
        nextDeadlineKind: "",
        exposureUsd: "12500.00",
      },
    ],
    ticketCount: 1,
    earliestDeadlineAt: null,
    earliestDeadlineKind: "",
  },
  // The feed's money: $835.27 on the $12,500 judgment, $3.08 a day.
  judgmentInterest: {
    totalUsd: "835.27",
    principalUsd: "12500.00",
    judgmentCount: 1,
    perDayUsd: "3.08",
    annualRate: "0.09",
  },
  engagements: [
    {
      id: `${CASE_ID}-ex`,
      matterId: CASE_ID,
      professionalType: "EXPEDITER",
      status: "REQUESTED",
      freeText: "",
      requestedAt: s.accepted,
      engagedAt: null,
      completedAt: null,
    },
  ],
  // "Chat 1" as the mockup; the demo's Chat tab never opens, so nothing can post.
  thread: [
    {
      id: `${CASE_ID}-m1`,
      matterId: CASE_ID,
      party: "codeclear",
      body: happening(s),
      visibility: "",
      relayedProfessionalType: "",
      relayedFrom: "",
      authorEmail: "",
      createdAt: s.accepted,
    },
  ],
});

export const isYcCase = (c: { caseId: string }): boolean =>
  c.caseId === CASE_ID;
/** Leo 10-31 R2: the demo case, or with the switch on any case holding the ticket, reads as judgment owed off. Per
 * case, so a real case's other judgments lose their copy too; demo-only, never on in prod. Pair with ycCaseStrip. */
export const ycCovers = (
  c: {
    caseId: string;
    spine?: { tickets?: readonly { ticketNumber: string }[] } | null;
  },
  ycOn: boolean,
): boolean =>
  isYcCase(c) ||
  (ycOn && !!c.spine?.tickets?.some((t) => t.ticketNumber === YC_TICKET));

/** The cases query's `select`: switch on and accepted adds the demo case to its building, first. Off, undefined:
 * a tab's saved demo state never reaches a docket once the switch is off. */
export const useYcCases = (
  buildingId: string | undefined,
): ((d: ViolationCasesResponse) => ViolationCasesResponse) | undefined => {
  const on = usePortalFeaturesQuery(false).data?.ycDemo === true;
  const s = useYc();
  return React.useMemo(
    () =>
      on && s.accepted && s.buildingId === buildingId
        ? (d: ViolationCasesResponse) => ({
            ...d,
            count: d.count + 1,
            cases: [ycCase(s), ...d.cases],
          })
        : undefined,
    [on, s, buildingId],
  );
};

/** A covered case as the server sends it with judgment owed off: no "Judgment owed" stage name, no ticket clock. */
export const ycCaseStrip = (c: ViolationCase): ViolationCase => {
  if (!c.spine) return c;
  const { spine } = c;
  return {
    ...c,
    spine: {
      ...spine,
      stages: spine.stages.map((st) =>
        st.label === JUDGMENT_OWED ? { ...st, label: null } : st,
      ),
      tickets: spine.tickets.map(
        (t) =>
          Object.fromEntries(
            Object.entries(t).filter(([k]) => !CLOCK_KEYS.has(k)),
          ) as typeof t,
      ),
    },
  };
};
